import React, { FC, useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Switch,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useHistory, Link } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useApolloClient } from "@apollo/client";
import { useAuthContext } from "context/AuthContext";
import { useTheme } from "@emotion/react";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { ColorModeContext } from "../../../Theme";
import useId from "@mui/material/utils/useId";
import styled from "@emotion/styled";
import { useProductTour } from "providers/ProductTour";
import ProductTourIcon from "assets/product-tour.svg";
import { ManageAccounts } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { DEFAULT_AVATAR } from "config";
import { useMeQuery } from "api";
import { MY_ACCOUNT_URL } from "config";
import FixedLoader from "components/FixedLoader";
import { accountSync } from "../accountSync";
import { toast } from "react-hot-toast";
import { USER_LOG_OUT } from "utills/events";
import ReactGA from "react-ga4";
import Avatar from "components/Avatar";
import { getIsRingVisible } from "utills";

const ProfileDropdown = () => {
  const auth = useAuth();
  const { handleModalOpen } = useProductTour();
  const [isLoading, setIsLoading] = useState(false);
  const theme: any = useTheme();
  const colorMode = React.useContext(ColorModeContext);
  const history = useHistory();
  const client = useApolloClient();
  const { user, removeUserInfo } = useAuthContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data } = useMeQuery();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRefersh = async () => {
    try {
      setAnchorEl(null);
      const user = await auth.signinSilent();
      if (user?.access_token) {
        setIsLoading(true);
        const { data } = await accountSync(user?.access_token);
        if (data?.data?.accountSync_V2?.userDetails?.canBeOnboarded) {
          history.replace(`/onboarding`);
        } else {
          (window as Window).location = window.location.href;
        }
      }
    } catch (e: any) {
      toast.error(e.resposne);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout: React.MouseEventHandler<HTMLLIElement> = async (e) => {
    e.preventDefault();
    ReactGA.event(USER_LOG_OUT, {});
    (window as any)?.dataLayer?.push({ event: "Logout" });
    removeUserInfo();
    client.clearStore();
    auth.signoutRedirect({
      id_token_hint: auth.user?.id_token,
    });
    history.replace("/");
  };

  const open = Boolean(anchorEl);
  if (isLoading) return <FixedLoader />;
  return (
    <Container>
      <Button
        id={"btnProfile"}
        aria-label="Profile Button"
        aria-controls={open ? "btnProfile" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          ".MuiButton-endIcon": {
            marginLeft: 0,
          },
        }}
      >
        <Avatar
          alt={`${data?.me?.firstName} ${data?.me?.lastName}`}
          src={data?.me?.avatar?.LowResUri || DEFAULT_AVATAR}
          hasRing={getIsRingVisible(data?.me.membershipLevel)}
          sx={{ width: 32, height: 32 }}
        />
      </Button>
      <Menu
        id={"btnProfile"}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "btnProfile",
        }}
        PaperProps={{
          style: {
            width: 240,
          },
        }}
        elevation={1}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          component={Link}
          to={`/profile/${user?.username}`}
          onClick={handleClose}
        >
          <ListItemIcon>
            <Avatar
              src={data?.me?.avatar?.LowResUri || DEFAULT_AVATAR}
              hasRing={getIsRingVisible(data?.me?.membershipLevel)}
            />
          </ListItemIcon>
          <ListItemText
            primary={`${data?.me?.firstName} ${data?.me?.lastName}`}
            secondary={`@${user?.username}`}
            sx={{ marginLeft: 1 }}
          />
        </MenuItem>

        <MenuItem
          component="a"
          href={MY_ACCOUNT_URL}
          target="__target"
          onClick={handleClose}
        >
          <ListItemIcon>
            <ManageAccounts />
          </ListItemIcon>
          <ListItemText>Manage Your Account</ListItemText>
        </MenuItem>
        <MenuItem component={Button} onClick={handleRefersh}>
          <ListItemIcon>
            <RefreshIcon />
          </ListItemIcon>
          <ListItemText>Refresh Your Account</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleModalOpen?.();
            handleClose();
          }}
        >
          <ListItemIcon>
            <ProductTourIcon />
          </ListItemIcon>
          <ListItemText>Product Tour</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to={`/help-centre`} onClick={handleClose}>
          <ListItemIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
            </svg>
          </ListItemIcon>
          <ListItemText>Help Centre</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to={`/terms`} onClick={handleClose}>
          <ListItemIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-book-open"
            >
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
          </ListItemIcon>
          <ListItemText>Terms of use</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to={`/privacy`} onClick={handleClose}>
          <ListItemIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-shield"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </ListItemIcon>
          <ListItemText>Privacy Policy</ListItemText>
        </MenuItem>
        <MenuItem
          component={Link}
          to={`/settings/blocked-users`}
          onClick={handleClose}
        >
          <ListItemIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-slash"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
            </svg>
          </ListItemIcon>
          <ListItemText>Blocked Users</ListItemText>
        </MenuItem>
        <MenuItem
          component={Link}
          to={`/settings-support`}
          onClick={handleClose}
        >
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText>Account Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={colorMode.toggleColorMode}>
          <ListItemIcon>
            {theme.palette.mode === "dark" ? (
              <Brightness7Icon />
            ) : (
              <Brightness4Icon />
            )}
          </ListItemIcon>
          <ListItemText>Dark Mode</ListItemText>
          <Switch
            checked={theme.palette.mode === "dark"}
            inputProps={{ "aria-label": "controlled" }}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-log-out"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ProfileDropdown;

const Container = styled.div`
  display: none;
  @media (min-width: 960px) {
    display: inline-block;
  }
`;
